import React, { ReactNode, useRef, useState } from "react";
import { Link } from "react-router-dom";

import css from "./DropdownMenu.module.css";

type Item = { label: string; to: string };

type Props = {
  label: ReactNode;
  items: Item[];
  themeVars?: React.CSSProperties;
  /** Accessible name for the submenu. */
  menuLabel?: string;
};

const DropdownMenu: React.FC<Props> = ({
  label,
  items,
  themeVars,
  menuLabel,
}) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const openNow = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };

  const closeSoon = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 140);
  };

  return (
    <li
      className={css.navItem}
      style={themeVars}
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      onFocus={openNow}
      onBlur={(e) => {
        // Close only when focus leaves the whole menu (keyboard users).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setOpen(false);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpen(false);
      }}
    >
      <span className={css.trigger}>{label}</span>
      <span className={css.bridge} aria-hidden />

      <div
        className={css.dropdown}
        role="group"
        aria-label={menuLabel}
        style={{
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transform: open ? "translateY(0)" : "translateY(6px)",
        }}
      >
        <ul className={css.menuList}>
          {items.map((it) => (
            <li key={it.to}>
              <Link
                className={css.menuLink}
                to={it.to}
                onClick={() => setOpen(false)}
              >
                {it.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
};

export default DropdownMenu;
